import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    /** Ẩn nút X mặc định (góc trên phải) — khi hộp thoại tự đặt nút đóng trong tiêu đề của nó. */
    hideClose?: boolean;
  }
>(({ className, children, hideClose, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Keyframe `enter`/`exit` của tailwindcss-animate GHI ĐÈ `transform`, bắt đầu từ translate(0,0)
        // thay vì translate(-50%,-50%) đang căn giữa — thiếu cặp `slide-*-left-1/2 / top-[48%]` thì
        // mọi hộp thoại trượt chéo ~nửa màn hình từ góc vào tâm lúc mở và ngược lại lúc đóng,
        // không mờ dần. Bộ lớp dưới là của shadcn gốc, giữ nguyên để mở/đóng là mờ + phóng nhẹ tại chỗ.
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      {!hideClose && <DialogCloseButton className="absolute right-3 top-3" />}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * Nút đóng chuẩn của hộp thoại — một kiểu cho toàn app, theo đúng ngôn ngữ icon button của dự án
 * (`Button variant="ghost" size="icon"` và nút đóng menu ở `TopBar`): bo góc `rounded-md`, không
 * viền, hover `bg-accent`, icon 20px.
 *
 * Đóng là hành động PHỤ (hành động chính nằm ở chân hộp thoại), nên là ghost: không tranh mắt với
 * nút chính. Vùng bấm 40×40 (≥ 24px tối thiểu của WCAG 2.5.8, ngang mức 44px của Apple HIG). Màu
 * xám → đậm khi rê chuột, `focus-visible` mới hiện ring (không chớp ring khi bấm chuột).
 * `aria-label` cho bộ đọc màn hình và `title` dạy phím tắt Esc.
 *
 * Mặc định `DialogContent` đặt nó `absolute` ở góc trên phải; hộp thoại có tiêu đề riêng
 * (`hideClose`) đặt nó trong hàng tiêu đề để căn giữa theo chiều cao tiêu đề.
 */
const DialogCloseButton = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    type="button"
    aria-label="Đóng"
    title="Đóng (Esc)"
    className={cn(
      "inline-flex size-10 shrink-0 items-center justify-center rounded-md text-muted-foreground ring-offset-background transition-colors",
      "hover:bg-accent hover:text-accent-foreground",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props}
  >
    <X className="size-5" aria-hidden="true" />
  </DialogPrimitive.Close>
));
DialogCloseButton.displayName = "DialogCloseButton";

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col space-y-1.5 text-left", className)} {...props} />;
}
function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />;
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogCloseButton,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
